/** @format */

import React, { useState } from 'react';
import { useAppContext, FILTER_NAMES } from '../../store/store.app';
import { Avatar, Box, IconButton, Tooltip, Typography } from '@mui/material';

import SidebarButton from '../Atoms/SidebarButton';
import AppFilter from './Sidebar/AppFilter';
import UserProfile from './UserProfile/UserProfile';
import useHotelNames, { getHotelDisplay } from '../../hooks/useHotelNames';

import logo from '../../Assets/img/BizBarometr_Logo.png';

export function MobileToolbar() {
  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const hotels = useHotelNames().filter(
    (hotel) => String(hotel?.name || '').toLowerCase() !== 'alembic'
  );

  return (
    <Box
      className="no-print"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: { xs: '54px', sm: '64px' },
        px: { xs: 1, sm: 1.5 },
        py: 0,
        background: '#ffffff',
        borderBottom: '1px solid #f0f1f3',
        boxShadow: '0px 2px 8px rgba(0,0,0,0.02)',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
          flex: 1,
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="BizBarometr By ALEMBIC"
          sx={{
            display: 'block',
            height: { xs: '26px', sm: '30px' },
            width: 'auto',
            maxWidth: '110px',
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />

        {hotels.length > 0 && (
          <>
            <Box
              sx={{
                height: '24px',
                width: '1px',
                background: '#e5e7eb',
                flexShrink: 0,
              }}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {hotels.map((hotel, i) => (
                <React.Fragment key={`${hotel.name}-${i}`}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#374151',
                        lineHeight: 1.2,
                      }}
                    >
                      {hotel.name}
                    </Typography>

                    {hotel.location && (
                      <Typography
                        noWrap
                        sx={{
                          fontSize: '10px',
                          fontWeight: 500,
                          color: '#6b7280',
                          lineHeight: 1.2,
                        }}
                      >
                        • {hotel.location}
                      </Typography>
                    )}
                  </Box>

                  {i < hotels.length - 1 && (
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 300,
                        color: '#d1d5db',
                        userSelect: 'none',
                        flexShrink: 0,
                      }}
                    >
                      &
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}
      >
        <AppFilter compact />

        <Tooltip title="Settings">
          <IconButton
            size="small"
            onClick={handleOpenUserMenu}
            sx={{
              p: { xs: 0.5, sm: 1 },
            }}
          >
            <Avatar
              alt="User Profile"
              sx={{
                width: { xs: 30, sm: 32 },
                height: { xs: 30, sm: 32 },
                bgcolor: '#f1f2ff',
                color: '#2D348D',
                fontSize: '14px',
                fontWeight: 600,
                border: '1px solid #dfe2ff',
              }}
            />
          </IconButton>
        </Tooltip>

        <UserProfile
          anchorElUser={anchorElUser}
          handleCloseUserMenu={handleCloseUserMenu}
        />
      </Box>
    </Box>
  );
}

export function DesktopToolbar() {
  const [anchorElUser, setAnchorElUser] = useState(null);

  const {
    appStore: { appFilters },
  } = useAppContext();

  const params = appFilters?.[FILTER_NAMES.PARAMETER] || [];

  const hotelParam = params.find(
    (p) => String(p?.name || '').toLowerCase() === 'hotel'
  );

  const selectedHotels = Array.isArray(hotelParam?.selected)
    ? hotelParam.selected.filter(Boolean)
    : [];

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <Box
      className="no-print"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: '64px',
        px: 3,
        background: '#ffffff',
        borderBottom: '1px solid #f0f1f3',
        boxShadow: '0px 2px 8px rgba(0,0,0,0.02)',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          height: '100%',
        }}
      >
        <SidebarButton
          sx={{
            color: '#282a2c',
            mr: 2,
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            mr: 3,
            width: '145px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="BizBarometr By ALEMBIC"
            sx={{
              display: 'block',
              width: '145px',
              height: 'auto',
              maxWidth: '145px',
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
        </Box>

        <Box
          sx={{
            height: '34px',
            width: '1px',
            background: '#e5e7eb',
            mr: 3,
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {selectedHotels.map((code, i) => {
            const hotel = getHotelDisplay(code);

            if (!hotel.name) {
              return null;
            }

            return (
              <React.Fragment key={`${code}-${i}`}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '20px',
                      fontWeight: 700,
                      color: '#374151',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {hotel.name}
                  </Typography>

                  {hotel.location && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: '#2D348D',
                          fontSize: '14px',
                          lineHeight: 0,
                        }}
                      >
                        •
                      </Box>

                      {hotel.location}
                    </Typography>
                  )}
                </Box>

                {i < selectedHotels.length - 1 && (
                  <Typography
                    sx={{
                      mx: 2.5,
                      fontSize: '18px',
                      fontWeight: 300,
                      color: '#d1d5db',
                      userSelect: 'none',
                    }}
                  >
                    &
                  </Typography>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexShrink: 0,
        }}
      >
        <Tooltip title="Account settings">
          <IconButton
            size="small"
            onClick={handleOpenUserMenu}
            sx={{
              ml: 1,
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: '#f1f2ff',
                color: '#2D348D',
                fontSize: '14px',
                fontWeight: 600,
                border: '1px solid #dfe2ff',
              }}
            />
          </IconButton>
        </Tooltip>

        <UserProfile
          anchorElUser={anchorElUser}
          handleCloseUserMenu={handleCloseUserMenu}
        />
      </Box>
    </Box>
  );
}
